<script setup lang="ts">
import { ref } from 'vue'
import { parseSave, SaveParseError } from '../../core/parser/saveParser'
import { useGameStore } from '../../stores/game'

const store = useGameStore()
const input = ref<HTMLInputElement | null>(null)
const isParsing = ref(false)
const errorMessage = ref('')

async function handleUpload(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  isParsing.value = true
  errorMessage.value = ''

  try {
    const data = await parseSave(file)
    store.setGame(data, file.name)
  } catch (error) {
    errorMessage.value = error instanceof SaveParseError
      ? error.message
      : '解析文件时发生未知错误。'
  } finally {
    isParsing.value = false
    target.value = ''
  }
}

function clearData() {
  store.reset()
  errorMessage.value = ''
  if (input.value) input.value.value = ''
}
</script>

<template>
  <section class="upload-panel" aria-labelledby="upload-title">
    <div>
      <p class="eyebrow">本地数据</p>
      <h2 id="upload-title">导入解析结果</h2>
      <p class="description">
        读取 CrashlandsTools v4 单文件协议（2.0.0）。文件只在当前浏览器中解析，
        不会上传到服务器。
      </p>
    </div>

    <div class="actions">
      <label class="file-button" :class="{ disabled: isParsing }">
        <span>{{ isParsing ? '解析中…' : '选择 JSON 文件' }}</span>
        <input
          ref="input"
          type="file"
          accept="application/json,.json"
          :disabled="isParsing"
          @change="handleUpload"
        />
      </label>

      <button v-if="store.loaded" class="secondary-button" type="button" @click="clearData">
        清除数据
      </button>
    </div>

    <p v-if="store.sourceFileName" class="file-name">
      已载入：{{ store.sourceFileName }}
    </p>
    <p v-if="store.game" class="protocol">
      存档 {{ store.game.capture.saveId }} · 工具 {{ store.game.capture.toolVersion }}
      · 协议 {{ store.game.capture.schemaVersion }}
      · 完整性 {{ store.game.capture.integrityStatus }}
    </p>
    <p v-if="errorMessage" class="error" role="alert">{{ errorMessage }}</p>
  </section>
</template>

<style scoped>
.upload-panel {
  display: grid;
  gap: 18px;
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: var(--panel);
  box-shadow: var(--shadow);
}

.eyebrow {
  margin: 0 0 6px;
  color: var(--accent-strong);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

h2,
.description,
.file-name,
.error {
  margin: 0;
}

h2 {
  font-size: 20px;
}

.description {
  margin-top: 8px;
  color: var(--muted);
  line-height: 1.65;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.file-button,
.secondary-button {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.file-button {
  display: inline-flex;
  align-items: center;
  color: #102113;
  background: var(--accent);
}

.file-button input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.file-button.disabled {
  cursor: wait;
  opacity: 0.65;
}

.secondary-button {
  border: 1px solid var(--line);
  color: var(--text);
  background: transparent;
}

.file-name {
  color: var(--muted);
  font-size: 14px;
}

.error {
  color: #ff9d9d;
}

.protocol {
  margin: -8px 0 0;
  color: #a6d5ad;
  font-size: 13px;
}
</style>
